import { useEffect } from 'react';
import PropTypes from 'react-proptypes';
import { connect } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  CurrencyInputComponent,
  SplitDateInputComponent,
  MessageBoxComponent,
  MessageBoxToggleComponent,
  ThemeComponent,
  SelectComponent,
} from '@dnhb/node_package';
import { dispatchMapper, INIT, SYNC } from '../../../shared/wizard/actions';
import { shapeInput, shapeOutput, shapeSettings } from '../config';
import Analytics from 'shared/utility/analytics';

const WizardMortgage = ({
  input,
  sync,
  pages,
  dispatch,
  recalculating,
  forward,
  output: { errors },
  settings: { mortgage_providers },
}) => {
  const { mortgage_provider } = input;
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (pages.length > 3 && typeof recalculating !== 'number') {
      dispatch({ type: INIT, location, navigate, payload: { pages: pages.slice(0, 3) } });
    }
  }, [pages, dispatch, recalculating]);

  useEffect(() => {
    sync('next_step_text', 'Berekenen');
    return () => sync('next_step_text', null);
  }, [sync]);

  useEffect(() => {
    dispatch({ type: SYNC, syncing: false });
    Analytics.send('Navigeer', 'Hypotheek formulier');
  }, [dispatch]);

  return (
    <form onSubmit={forward(recalculating, location, navigate)}>
      <input type="submit" className="hidden" />
      <div className="wrapper">
        <h2>Uw hypotheek</h2>
        <div className="row">
          <div className="group col-xs-12">
            <h3>Uw geldverstrekker</h3>
            <ThemeComponent className="select-input" errors={errors.mortgage_provider}>
              <SelectComponent
                id="mortgage_provider"
                options={mortgage_providers}
                value={mortgage_provider}
                onChange={update => sync('mortgage_provider', update)}
                isSearchable
                autoFocus
                label="Zoeken"
              />
            </ThemeComponent>
          </div>
        </div>
        <div className="row">
          <div className="group col-xs-12">
            <h3>
              Wat is uw aflossingsvrije hypotheekschuld?
              <MessageBoxToggleComponent
                active={input.info_box === 'mortgage_debt'}
                onClickHandler={() => sync('info_box', input.info_box === 'mortgage_debt' ? null : 'mortgage_debt')}
              />
            </h3>
            <MessageBoxComponent
              open={input.info_box === 'mortgage_debt'}
              onCloseHandler={() => sync('info_box', null)}
            >
              Vul hier de actuele restschuld van uw aflossingsvrije (deel)lening in. Deze kan afwijken van de
              oorspronkelijke hypotheekschuld als u één of meerdere aflossingen heeft gedaan.
            </MessageBoxComponent>
            <CurrencyInputComponent
              id="interest_only_mortgage_debt"
              onChangeHandler={sync('interest_only_mortgage_debt')}
              value={input.interest_only_mortgage_debt}
              errors={errors.interest_only_mortgage_debt}
              label="Bedrag"
            />
          </div>
        </div>
        <div className="row">
          <div className="group col-xs-12">
            <h3>
              Wat is de einddatum van uw hypotheek?
              <MessageBoxToggleComponent
                active={input.info_box === 'end_date'}
                onClickHandler={() => sync('info_box', input.info_box === 'end_date' ? null : 'end_date')}
              />
            </h3>
            <MessageBoxComponent open={input.info_box === 'end_date'} onCloseHandler={() => sync('info_box', null)}>
              Vul hier de einddatum van uw aflossingsvrije leningdeel in. Dit is de datum waarop u de lening uiterlijk
              moet terugbetalen.
            </MessageBoxComponent>
            <div className="row">
              <SplitDateInputComponent
                name="mortgage_end_date"
                onChangeHandler={sync('mortgage_end_date')}
                day={input.mortgage_end_date.day}
                month={input.mortgage_end_date.month}
                year={input.mortgage_end_date.year}
                errors={errors.mortgage_end_date}
              />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="group col-xs-12">
            <h3>
              Hoeveel is uw woning momenteel waard?
              <MessageBoxToggleComponent
                active={input.info_box === 'home_value'}
                onClickHandler={() => sync('info_box', input.info_box === 'home_value' ? null : 'home_value')}
              />
            </h3>
            <MessageBoxComponent open={input.info_box === 'home_value'} onCloseHandler={() => sync('info_box', null)}>
              Mogelijk heeft u uw woning de afgelopen jaren laten taxeren. Anders kunt u een schatting doen op basis van
              de waarde van verkochte woningen in uw omgeving. Ook kan u adviseur u helpen met een modelmatige
              waardebepaling (dit is een geschatte waarde op basis van een computermodel). Ten slotte kunt u ook de WOZ
              waarde nemen, maar die zal doorgaans lager zijn dan de marktwaarde.
            </MessageBoxComponent>
            <CurrencyInputComponent
              id="home_value"
              onChangeHandler={sync('home_value')}
              value={input.home_value}
              errors={errors.home_value}
              label="Bedrag"
            />
          </div>
        </div>
      </div>
    </form>
  );
};

WizardMortgage.propTypes = {
  sync: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  forward: PropTypes.func.isRequired,
  recalculating: PropTypes.number,
  input: shapeInput.isRequired,
  output: shapeOutput.isRequired,
  settings: shapeSettings.isRequired,
  pages: PropTypes.arrayOf(PropTypes.string).isRequired,
};

WizardMortgage.defaultProps = {
  recalculating: null,
};

export default connect(
  state => ({
    recalculating: state.recalculating,
    syncing: state.syncing,
    input: state.input,
    output: state.output,
    settings: state.settings,
    pages: state.pages,
  }),
  dispatchMapper
)(WizardMortgage);
